import { useFragment, useMutation } from '@apollo/client/react';
import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightIcon, SaveIcon } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { type ICustomIntegration, type ICustomIntegrationUpdateInputAttributes } from 'src/graphql/schema';
import { UpdateCustomIntegrationDocument } from 'src/graphql/actions/integration/updateCustomIntegration.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { renderLogo } from 'src/utils/renderLogo';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { CustomIntegrationFragment } from 'src/graphql/actions/integration/integration.fragment';
import { SetupSteps } from 'src/components/Integration/Setup';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

import { useIntegration, customIntegrationSetupMap } from '../model';

import { customIntegrationInstructionUpdateMap as instructionMap } from './customIntegrationInstructionMap';

type IStep0Data = {
  key: 0;
  items: React.ReactNode[];
};

type IStep1Data = {
  key: 1;
  form: React.ComponentType<{
    onSubmit: (values: unknown) => Promise<void>;
    onBack: () => void;
    accountName?: string;
    accountUrl?: string;
    btnText?: string | React.ReactNode;
  }>;
  accountName?: string;
  accountUrl?: string;
};

type IStepData = (IStep0Data | IStep1Data) & { title: string; description: string; modalWidth: string };

const StepRenderer = ({ stepData, setStep }: { stepData: IStepData; setStep: Dispatch<SetStateAction<number>> }) => {
  const {
    update: { integrationId },
    resetIntegration,
  } = useIntegration();
  const [updateCustomIntegration] = useMutation(UpdateCustomIntegrationDocument);

  if (!integrationId) return null;

  switch (stepData.key) {
    case 0: {
      const handleClick = () => setStep(1);

      return (
        <Fragment>
          <SetupSteps items={stepData.items} />
          <Button onClick={handleClick} className="w-full" size="sm">
            Update credentials
            <ArrowRightIcon />
          </Button>
        </Fragment>
      );
    }
    case 1: {
      const onSubmit = async (values: unknown) => {
        try {
          const { data } = await updateCustomIntegration({
            variables: { customIntegrationInput: values as ICustomIntegrationUpdateInputAttributes, integrationId },
          });

          if (data?.updateCustomIntegration?.success) {
            toast.success('Credentials updated');
            resetIntegration();
          } else {
            throw new ClientError(data?.updateCustomIntegration?.errors?.[0]);
          }
        } catch (error) {
          getApiError(error, toast.error);
        }
      };
      const handleBack = () => setStep(0);
      const Form = stepData.form;
      return (
        <Form
          onSubmit={onSubmit}
          onBack={handleBack}
          accountName={stepData.accountName || ''}
          accountUrl={stepData.accountUrl || ''}
          btnText={
            <Fragment>
              <SaveIcon />
              Save updated credentials
            </Fragment>
          }
        />
      );
    }
    default:
      return null;
  }
};

const Container = () => {
  const [step, setStep] = useState(0);
  const {
    update: { integrationId },
  } = useIntegration();
  const integration = useFragment<ICustomIntegration>({
    fragment: CustomIntegrationFragment,
    from: {
      __typename: 'CustomIntegration',
      id: integrationId || '',
    },
  });

  if (!integration?.data || !integrationId) return null;

  const { provider, accountName, accountUrl } = integration.data;

  const getStepData = (): IStepData => {
    switch (step) {
      case 0: {
        const data = instructionMap[provider as keyof typeof instructionMap];
        return {
          key: 0,
          modalWidth: 'md:max-w-2xl',
          ...data,
        };
      }
      case 1: {
        const data = customIntegrationSetupMap[provider as keyof typeof customIntegrationSetupMap];
        return {
          key: 1,
          ...data,
          modalWidth: 'md:max-w-xl',
          accountName: accountName ?? '',
          accountUrl: (accountUrl ?? '').replace(/^https?:\/\//, ''),
        };
      }
      default:
        return {
          key: 0,
          title: '',
          description: '',
          modalWidth: 'md:max-w-0',
          items: [],
        };
    }
  };

  const Icon = renderLogo(provider as keyof typeof PROVIDER_KEY_TO_NAME);
  const stepData = getStepData();

  return (
    <DialogContent className={cn('gap-6 max-h-205 overflow-y-auto', stepData.modalWidth)}>
      <DialogHeader className={cn('relative text-center gap-0', step === 1 && 'pl-16 text-left')}>
        {Icon && <Icon className={cn('size-10 mx-auto mb-1.5', step === 1 && 'absolute top-0 left-0')} />}
        <DialogTitle className={cn('text-xl font-bold mb-4 leading-5', step === 1 && 'mb-1.5')}>
          {stepData.title}
        </DialogTitle>
        <DialogDescription className="font-medium">{stepData.description}</DialogDescription>
      </DialogHeader>
      <StepRenderer stepData={stepData} setStep={setStep} />
    </DialogContent>
  );
};

const UpdateCustomIntegrationDialog = () => {
  const { update, resetIntegration } = useIntegration();

  const onOpenChange = (open: boolean) => {
    if (!open) resetIntegration();
  };

  return (
    <Dialog open={update.isOpen} onOpenChange={onOpenChange}>
      <Container />
    </Dialog>
  );
};

export default UpdateCustomIntegrationDialog;
